<script setup lang="ts">
import { computed, inject, ref } from 'vue'
import BbelField from '@/views/builder/components/bbel/BbelField.vue'
import ConnectionPicker from '../ConnectionPicker.vue'
import HeadersEditor from './HeadersEditor.vue'
import BodyEditorText from './BodyEditorText.vue'
import BodyEditorUrlEncoded from './BodyEditorUrlEncoded.vue'
import BodyEditorMultipart from './BodyEditorMultipart.vue'
import BodyEditorBinary from './BodyEditorBinary.vue'
import ValidatorPipelineEditor from './ValidatorPipelineEditor.vue'
import FilterWhenField from '../FilterWhenField.vue'
import type {
  HttpServiceModelBase,
  HttpMethod,
  IBodyContent,
  TextBodyContent,
  UrlEncodedBodyContent,
  MultipartFormDataBodyContent,
  BinaryBodyContent,
} from '@/types'
import { HttpMethodsList, HttpVersionEnum } from '@/types'
import { BBEL_SCOPE_KEY, EMPTY_BBEL_SCOPE, type BbelScopeContext } from '@/views/builder/composables/bbel-context'
import { EDITED_SERVICE_KEY, type EditedServiceContext } from '@/views/builder/composables/edited-service-context'
import {
  CONNECTION_CONTEXT_KEY,
  type BuilderConnectionContext,
} from '@/views/builder/composables/connection-context'
import { useConnectionTemplate } from '@/queries/connection-templates'
import { baseUrlVariants, chipSegments, ghostHeaderRows, uniqueBaseUrl, type ChipSegment } from './connection-preview'
import { advancedCount } from './advanced-count'
import type { ScopeDescriptor } from '@/types/bbel-types'

const props = defineProps<{
  modelValue: HttpServiceModelBase
}>()

const emit = defineEmits<{
  'update:modelValue': [value: HttpServiceModelBase]
}>()

// #5683 — the BBEL editor scope for the URL field, provided by BuilderView (empty in read-only hosts).
const bbelScopeCtx = inject<BbelScopeContext | undefined>(BBEL_SCOPE_KEY, undefined)
const scope = computed<ScopeDescriptor>(() => bbelScopeCtx?.value ?? EMPTY_BBEL_SCOPE)

// Filter when is a property of the service, not of this model, so it is read and written through the edited service.
const editedService = inject<EditedServiceContext | undefined>(EDITED_SERVICE_KEY, undefined)
const filterWhen = computed({
  get: () => editedService?.value?.serviceFilterExpression ?? '',
  set: (expression: string) => {
    if (!editedService?.value) return
    editedService.value = { ...editedService.value, serviceFilterExpression: expression }
  },
})

const contentTypes = [
  'application/json',
  'application/x-www-form-urlencoded',
  'multipart/form-data',
  'application/octet-stream',
  'text/plain',
  'text/xml',
  'text/xml; charset=utf-8',
  'application/xml',
  'application/soap+xml; charset=utf-8',
  'text/html',
]

// Maps content-type → bodyContent.$type discriminator (matches PolymorphicTypeRegistry.cs)
const bodyTypeMap: Record<string, string> = {
  'application/json': 'text',
  'application/x-www-form-urlencoded': 'urlEncoded',
  'multipart/form-data': 'multipart',
  'application/octet-stream': 'binary',
  'text/plain': 'text',
  'text/xml': 'text',
  'application/xml': 'text',
  'text/html': 'text',
}

// The body-editor kind keys off the base media type only, so a parameterised header like
// `text/xml; charset=utf-8` (SOAP) resolves to its base `text/xml`; an unrecognised type is text.
function bodyTypeFor(contentType: string): string {
  const base = (contentType.split(';')[0] ?? '').trim().toLowerCase()
  return bodyTypeMap[base] || 'text'
}

const httpVersionOptions: Array<{ value: HttpVersionEnum; label: string }> = [
  { value: HttpVersionEnum.Version10, label: '1.0' },
  { value: HttpVersionEnum.Version11, label: '1.1' },
  { value: HttpVersionEnum.Version20, label: '2.0' },
  { value: HttpVersionEnum.Version30, label: '3.0' },
]

function updateModel(updates: Partial<HttpServiceModelBase>) {
  emit('update:modelValue', { ...props.modelValue, ...updates })
}

const currentBodyContent = computed<IBodyContent>(
  () => props.modelValue.bodyContent || { $type: 'none' },
)

// The value kind(s) a content type accepts (ADR-034), tagged onto each dropdown option so the
// expression-vs-binary capability is visible while choosing.
function contentTypeMode(contentType: string): string {
  switch (bodyTypeFor(contentType)) {
    case 'binary':
      return 'binary'
    case 'multipart':
      return 'text + binary'
    default:
      return 'text'
  }
}

// Build a fresh, properly-shaped IBodyContent for the given discriminator.
// Required so reloads don't crash on missing arrays.
function makeEmptyBody(bodyType: string, carryText = ''): IBodyContent {
  switch (bodyType) {
    case 'text':
      return { $type: 'text', textContent: carryText } as TextBodyContent
    case 'urlEncoded':
      return { $type: 'urlEncoded', bodyFormUrlEncodedContent: [] } as UrlEncodedBodyContent
    case 'multipart':
      return { $type: 'multipart', multipartFormContent: [] } as MultipartFormDataBodyContent
    case 'binary':
      return {
        $type: 'binary',
        source: { serviceId: '', projection: 'Output', path: [] },
      } as BinaryBodyContent
    default:
      return { $type: 'none' } as IBodyContent
  }
}

// Free-text content type (datalist combobox): the input fires per keystroke, so rebuild the body
// only when the body *kind* changes — typing within the text family (json → xml → soap) keeps the
// authored body; switching to/from multipart / binary / url-encoded swaps the editor.
function updateBodyMediaTypeHeader(contentType: string) {
  const newBodyType = bodyTypeFor(contentType)
  if (newBodyType === currentBodyContent.value.$type) {
    updateModel({ bodyMediaTypeHeader: contentType })
    return
  }
  const carryText =
    currentBodyContent.value.$type === 'text'
      ? (currentBodyContent.value as TextBodyContent).textContent || ''
      : ''
  const newBodyContent =
    newBodyType === 'text' ? makeEmptyBody('text', carryText) : makeEmptyBody(newBodyType)
  updateModel({ bodyMediaTypeHeader: contentType, bodyContent: newBodyContent })
}

function updateBodyContent(value: IBodyContent) {
  updateModel({ bodyContent: value })
}

const bodyMethods = ['POST', 'PUT', 'PATCH']

// Switching to a body-carrying method defaults an unset content type to JSON (the 90% case);
// an explicit earlier choice is never overwritten.
function updateHttpMethod(method: HttpMethod) {
  if (bodyMethods.includes(method) && !props.modelValue.bodyMediaTypeHeader) {
    updateModel({
      httpMethod: method,
      bodyMediaTypeHeader: 'application/json',
      bodyContent: makeEmptyBody('text'),
    })
    return
  }
  updateModel({ httpMethod: method })
}

// What the bound connection contributes (ADR-030), shown from the slot's template detail. The
// auth method lives on the connection (project-bound), so display dedupes across the template's
// methods and falls back where they disagree — see connection-preview.ts.
const connCtx = inject<BuilderConnectionContext | undefined>(CONNECTION_CONTEXT_KEY, undefined)
const boundTemplateId = computed(() => {
  const name = props.modelValue.connectionName
  return (name && connCtx?.connectionSlots.value.find((s) => s.name === name)?.connectionTemplateId) || ''
})
const { template } = useConnectionTemplate(boundTemplateId)
const templateMethods = computed(() => template.value?.authMethods ?? [])

// The base-URL chip: a relative URL composes onto the connection's baseUrl at runtime, so it shows
// as a readonly prefix INSIDE the URL input-group. Unambiguous template baseUrl → verbatim (its
// BBEL source, never a resolved value); methods disagree (or detail still loading) → the slot
// name, with the per-method values in the tooltip. An absolute URL is sent literally — no chip.
const urlIsAbsolute = computed(() => /^https?:\/\//i.test((props.modelValue.targetUrl ?? '').trim()))
const baseChip = computed<{ segments: ChipSegment[]; title: string } | null>(() => {
  const name = props.modelValue.connectionName
  if (urlIsAbsolute.value || !name) return null

  const base = uniqueBaseUrl(templateMethods.value)
  if (base) {
    return {
      segments: chipSegments(base.replace(/\/+$/, '') + '/'),
      title: `Base URL from connection '${name}'${template.value ? ` · ${template.value.name}` : ''}`,
    }
  }

  const variants = baseUrlVariants(templateMethods.value)
  return {
    segments: [{ kind: 'text', value: `${name}/` }],
    title: variants.length
      ? `Base URL depends on the connection's auth method:\n${variants.map((v) => `${v.method} → ${v.baseUrl}`).join('\n')}`
      : `Base URL resolved from the '${name}' connection`,
  }
})

// Readonly rows for the headers the connection injects; a user header with the same name is
// overridden at runtime (the marker inside HeadersEditor carries that warning).
const connectionHeaders = computed(() =>
  props.modelValue.connectionName ? ghostHeaderRows(templateMethods.value) : [],
)

const advancedOpen = ref(false)
const advancedSetCount = computed(() => advancedCount(props.modelValue, filterWhen.value))
const validationOpen = ref(false)
const ruleCount = computed(() => (props.modelValue.validatorPipeline ?? []).length)

// A transient retry resends the same request, so it can double-submit a mutation. Warn when the
// method changes data and retries are on — surfaced by the Method field, not buried in Advanced.
const mutatingMethods = ['POST', 'PUT', 'PATCH', 'DELETE']
const showMutationRetryWarning = computed(
  () => mutatingMethods.includes(props.modelValue.httpMethod) && props.modelValue.requestIsRepeatable,
)

</script>

<template>
  <div>
    <!-- The connection leads: it decides the base URL and auth, so everything below reads in its
         context (the URL field's base chip, the auth-collision warning). -->
    <ConnectionPicker
      :model-value="modelValue.connectionName"
      @update:model-value="updateModel({ connectionName: $event })"
    />

    <!-- HTTP Configuration -->
    <div class="row mb-3">
      <div class="col-md-2">
        <label class="form-label">
          Method
          <i
            v-if="showMutationRetryWarning"
            class="bi bi-exclamation-triangle-fill text-warning small ms-1"
            title="This request can be sent more than once if a transient retry fires. Make sure repeat calls are safe here — otherwise turn off transient retries in Advanced settings."
          ></i>
        </label>
        <select
          :value="modelValue.httpMethod"
          @change="updateHttpMethod(($event.target as HTMLSelectElement).value as HttpMethod)"
          class="form-select"
        >
          <option v-for="method in HttpMethodsList" :key="method" :value="method">
            {{ method }}
          </option>
        </select>
      </div>
      <div class="col-md-10">
        <label class="form-label">URL</label>
        <div class="input-group bbel-url-group">
          <span v-if="baseChip" class="input-group-text font-monospace" :title="baseChip.title">
            <template v-for="(seg, i) in baseChip.segments" :key="i"
              ><span :class="{ 'bbel-chip-expr': seg.kind === 'expr' }">{{ seg.value }}</span></template
            >
          </span>
          <BbelField
            :model-value="modelValue.targetUrl"
            @update:model-value="updateModel({ targetUrl: $event })"
            :scope="scope"
            single-line
            :placeholder="baseChip ? 'endpoint' : 'https://api.example.com/endpoint'"
          />
        </div>
      </div>
    </div>

    <HeadersEditor
      :model-value="modelValue.requestHeaders"
      :connection-headers="connectionHeaders"
      :connection-name="modelValue.connectionName"
      @update:model-value="updateModel({ requestHeaders: $event })"
    />

    <!-- Content Type & Body (only for methods that send bodies) -->
    <div v-if="bodyMethods.includes(modelValue.httpMethod)">
      <div class="mb-3">
        <label class="form-label">Content Type</label>
        <input
          :value="modelValue.bodyMediaTypeHeader"
          @input="updateBodyMediaTypeHeader(($event.target as HTMLInputElement).value)"
          list="http-content-types"
          class="form-control font-monospace"
          placeholder="application/json"
          autocomplete="off"
        />
        <datalist id="http-content-types">
          <option v-for="type in contentTypes" :key="type" :value="type"></option>
        </datalist>
        <div class="form-text">Body editor: {{ contentTypeMode(modelValue.bodyMediaTypeHeader) }}</div>
      </div>

      <BodyEditorText
        v-if="currentBodyContent.$type === 'text'"
        :model-value="(currentBodyContent as TextBodyContent).textContent || ''"
        :content-type="modelValue.bodyMediaTypeHeader"
        @update:model-value="updateBodyContent({ $type: 'text', textContent: $event } as TextBodyContent)"
      />
      <BodyEditorUrlEncoded
        v-else-if="currentBodyContent.$type === 'urlEncoded'"
        :model-value="currentBodyContent as UrlEncodedBodyContent"
        @update:model-value="updateBodyContent"
      />
      <BodyEditorMultipart
        v-else-if="currentBodyContent.$type === 'multipart'"
        :model-value="currentBodyContent as MultipartFormDataBodyContent"
        @update:model-value="updateBodyContent"
      />
      <BodyEditorBinary
        v-else-if="currentBodyContent.$type === 'binary'"
        :model-value="currentBodyContent as BinaryBodyContent"
        @update:model-value="updateBodyContent"
      />
    </div>

    <!-- Response validation: author rules over the response (#6022). A quiet disclosure like Advanced;
         the rule count shows when collapsed so authored rules aren't hidden. -->
    <div class="border-top mt-4 pt-2">
      <button
        type="button"
        class="btn btn-link text-secondary text-decoration-none px-0"
        @click="validationOpen = !validationOpen"
      >
        <i class="bi small me-1" :class="validationOpen ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
        Response validation
        <span v-if="ruleCount" class="badge text-bg-secondary ms-1">{{ ruleCount }}</span>
        <i
          class="bi bi-info-circle small ms-1 text-secondary"
          title="Validators run top to bottom; the first match decides the outcome."
        ></i>
      </button>
      <div v-show="validationOpen" class="mt-2">
        <div
          v-if="modelValue.regardHttpErrorAsFatal"
          class="alert alert-info py-2 px-3 small d-flex align-items-center gap-2 mb-2"
        >
          <i class="bi bi-info-circle flex-shrink-0"></i>
          <span>Error responses (4xx/5xx) already fail this service — add validators only for what that misses.</span>
        </div>
        <ValidatorPipelineEditor
          :model-value="modelValue.validatorPipeline"
          :scope="scope"
          @update:model-value="updateModel({ validatorPipeline: $event })"
        />
      </div>
    </div>

    <!-- Advanced settings: a quiet disclosure, not a card — the secondary tier must not outrank
         the primary fields above it. -->
    <div class="border-top mt-4 pt-2">
      <button
        type="button"
        class="btn btn-link text-secondary text-decoration-none px-0"
        @click="advancedOpen = !advancedOpen"
      >
        <i class="bi small me-1" :class="advancedOpen ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
        Advanced settings
        <span v-if="advancedSetCount" class="badge text-bg-secondary ms-1">{{ advancedSetCount }}</span>
      </button>
      <div v-show="advancedOpen">
        <div class="row mt-2">
          <div class="col-md-6 mb-2">
            <label class="form-label">Timeout (seconds)</label>
            <input
              type="text"
              class="form-control"
              :value="modelValue.requestTimeout"
              @input="updateModel({ requestTimeout: ($event.target as HTMLInputElement).value })"
              placeholder="00:01:40"
            />
            <div class="form-text">Format: HH:MM:SS</div>
          </div>
          <div class="col-md-6 mb-2">
            <label class="form-label">HTTP Version</label>
            <select
              :value="modelValue.httpVersion ?? 'Version20'"
              @change="updateModel({ httpVersion: ($event.target as HTMLSelectElement).value as HttpVersionEnum })"
              class="form-select"
            >
              <option v-for="opt in httpVersionOptions" :key="opt.value" :value="opt.value">
                {{ opt.label }}
              </option>
            </select>
          </div>
          <div class="col-md-6 mb-2">
            <div class="form-check form-switch">
              <input
                class="form-check-input"
                type="checkbox"
                :checked="modelValue.regardHttpErrorAsFatal"
                @change="updateModel({ regardHttpErrorAsFatal: ($event.target as HTMLInputElement).checked })"
                id="regardHttpErrorAsFatal"
              />
              <label class="form-check-label" for="regardHttpErrorAsFatal">
                Fail on error responses (4xx/5xx)
                <i
                  class="bi bi-info-circle small ms-1 text-secondary"
                  title="When off, error responses don't fail the service — your validators decide the outcome."
                ></i>
              </label>
            </div>
          </div>
          <div class="col-md-6 mb-2">
            <div class="form-check form-switch">
              <input
                class="form-check-input"
                type="checkbox"
                :checked="modelValue.requestIsRepeatable"
                @change="updateModel({ requestIsRepeatable: ($event.target as HTMLInputElement).checked })"
                id="requestRepeatable"
              />
              <label class="form-check-label" for="requestRepeatable">
                Automatically Retry Transient Errors
                <i
                  class="bi bi-info-circle small ms-1 text-secondary"
                  title="On (default): also retry failures where the request may have reached the server — a 5xx, a dropped response, or a timeout — which can resend it. Connection errors that never reached the server, and 429/503, always retry regardless."
                ></i>
              </label>
            </div>
          </div>
          <div class="col-md-6 mb-2">
            <div class="form-check form-switch">
              <input
                class="form-check-input"
                type="checkbox"
                :checked="modelValue.disableChunking"
                @change="updateModel({ disableChunking: ($event.target as HTMLInputElement).checked })"
                id="disableChunking"
              />
              <label class="form-check-label" for="disableChunking">
                Disable chunking
                <i
                  class="bi bi-info-circle small ms-1 text-secondary"
                  title="Send a fixed Content-Length instead of chunked transfer encoding. Enable for APIs that reject chunked request bodies."
                ></i>
              </label>
            </div>
          </div>
        </div>
        <FilterWhenField v-model="filterWhen" :scope="scope" />
      </div>
    </div>
  </div>
</template>

<style scoped>
/* BbelField isn't a .form-control, so the input-group's grow/corner rules are restated for it:
   the field takes the remaining width, and its left corners square off against the base chip. */
/* The chip keeps one control-height and top-aligns — it must not balloon with a soft-wrapped URL. */
.bbel-url-group {
  align-items: flex-start;
}
.bbel-url-group :deep(.bbel-field-wrap) {
  flex: 1 1 auto;
  width: 1%;
  min-width: 0;
}
.bbel-url-group :deep(.bbel-field-wrap:not(:first-child) .bbel-field),
.bbel-url-group :deep(.bbel-field-wrap:not(:first-child) .cm-editor) {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
/* Expression parts of the base-URL template — link-colour tracks light/dark like BBEL slots. */
.bbel-chip-expr {
  color: var(--bs-link-color);
}
</style>
